/** Global platform timezone for CDRs, billing, reports, and all panel date boundaries. */
export const PLATFORM_TIMEZONE = 'America/New_York'

const DEFAULT_TZ = PLATFORM_TIMEZONE

let activePanelTimezone: string = DEFAULT_TZ

/** Platform uses a single global timezone; per-account values are ignored for display/scoping. */
export function setActivePanelTimezone(_timezone?: string | null) {
  activePanelTimezone = DEFAULT_TZ
}

export function getActivePanelTimezone(): string {
  return activePanelTimezone
}

export function getTimezoneShortLabel(timeZone: string, date = new Date()): string {
  const tz = (timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz
  } catch {
    return tz
  }
}

export function formatDateTimeInPanelTz(
  value?: string | null,
  timeZone: string = getActivePanelTimezone()
): string {
  if (!value?.trim()) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(parsed)
  } catch {
    return parsed.toLocaleString()
  }
}

export function formatDateInPanelTz(
  value?: string | null,
  timeZone: string = getActivePanelTimezone()
): string {
  if (!value?.trim()) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(parsed)
  } catch {
    return parsed.toLocaleDateString()
  }
}

export function formatClockInPanelTz(
  date: Date,
  timeZone: string = getActivePanelTimezone()
): { time: string; date: string; weekday: string; abbrev: string } {
  const tz = (timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ
  try {
    const time = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date)
    const day = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
    }).format(date)
    const weekday = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      weekday: 'short',
    }).format(date)
    return {
      time,
      date: day,
      weekday,
      abbrev: getTimezoneShortLabel(tz, date),
    }
  } catch {
    return {
      time: date.toLocaleTimeString(undefined, { hour12: false }),
      date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
      abbrev: tz,
    }
  }
}
